import type { AxiosInstance } from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { apiErrorMessage } from "./api.js";
import {
  createExport,
  fetchDownloadUrl,
  fetchExportStatus,
  triggerBrowserDownload,
  type ExportFormat,
  type ExportStatus,
} from "./exports.js";
import type { ReportDefinition } from "./reports.js";

/**
 * The export tray's state, held ABOVE the keyed report screens so a job started on one
 * report survives navigating to another. The screens remount on every switch (that is
 * deliberate — see DashboardShell); anything that must outlive them lives here.
 *
 * POLLING, NOT PUSH. One interval (~2.5s) asks `/export/status/:id` for every job that
 * is still queued or processing, and stops the moment none are — the same plain
 * setInterval pattern OnboardingScreen uses; this app has no react-query and a websocket
 * would be a new server surface for a progress bar.
 */

export interface TrackedExport {
  readonly id: string;
  readonly tenantId: string;
  readonly reportKey: string;
  readonly label: string;
  readonly format: ExportFormat;
  readonly status: ExportStatus;
  readonly progress: number;
  readonly rowCount: number | null;
  readonly error: string | null;
  /** A READY job that hit the row ceiling — shown as an amber note, never hidden. */
  readonly truncated: boolean;
  /** The one automatic download has happened; further ones are the merchant's clicks. */
  readonly downloaded: boolean;
}

export interface StartExportInput {
  readonly report: ReportDefinition;
  readonly format: ExportFormat;
  readonly from: string;
  readonly to: string;
  readonly filters: Readonly<Record<string, unknown>>;
  readonly columns: readonly string[];
}

export interface ExportsContextValue {
  /** Jobs for the CURRENT store only — another store's exports are its own business. */
  readonly jobs: readonly TrackedExport[];
  readonly startExport: (input: StartExportInput) => Promise<void>;
  readonly download: (id: string) => Promise<void>;
  readonly dismiss: (id: string) => void;
  readonly clearFinished: () => void;
}

const ExportsContext = createContext<ExportsContextValue | null>(null);

/** Null outside a provider — so a screen rendered alone (tests) simply hides the button. */
export function useExports(): ExportsContextValue | null {
  return useContext(ExportsContext);
}

const POLL_MS = 2_500;

function isTerminal(status: ExportStatus): boolean {
  return status === "ready" || status === "failed";
}

export function ExportsProvider({
  client,
  tenantId,
  children,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string | null;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const [all, setAll] = useState<readonly TrackedExport[]>([]);
  /**
   * The poller reads the CURRENT list through a ref so the interval does not restart on
   * every progress tick — restarting it each render is how a 2.5s poll quietly becomes
   * a poll-per-render.
   */
  const allRef = useRef(all);
  allRef.current = all;

  const patch = useCallback((id: string, changes: Partial<TrackedExport>) => {
    setAll((current) => current.map((job) => (job.id === id ? { ...job, ...changes } : job)));
  }, []);

  const download = useCallback(
    async (id: string) => {
      // ALWAYS a fresh link — the stored one may be hours past its signature.
      const url = await fetchDownloadUrl(client, id);
      triggerBrowserDownload(url);
    },
    [client],
  );

  const startExport = useCallback(
    async (input: StartExportInput) => {
      if (tenantId === null) return;
      try {
        const job = await createExport(client, {
          reportType: input.report.key,
          from: input.from,
          to: input.to,
          format: input.format,
          filters: input.filters,
          columns: input.columns,
        });
        setAll((current) => [
          {
            id: job.id,
            tenantId,
            reportKey: input.report.key,
            label: input.report.label,
            format: input.format,
            status: "queued",
            progress: 0,
            rowCount: null,
            error: null,
            truncated: false,
            downloaded: false,
          },
          ...current,
        ]);
      } catch (caught) {
        /**
         * A refused export (the in-flight cap's 429, a 402 paywall, a validation 400)
         * still lands in the tray as a failed row — the merchant clicked a button and
         * MUST see the answer where they will look for the file, not in a console.
         */
        setAll((current) => [
          {
            id: `refused-${String(Date.now())}`,
            tenantId,
            reportKey: input.report.key,
            label: input.report.label,
            format: input.format,
            status: "failed",
            progress: 0,
            rowCount: null,
            error: apiErrorMessage(caught, "Could not start this export."),
            truncated: false,
            downloaded: false,
          },
          ...current,
        ]);
      }
    },
    [client, tenantId],
  );

  const dismiss = useCallback((id: string) => {
    setAll((current) => current.filter((job) => job.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setAll((current) => current.filter((job) => !isTerminal(job.status)));
  }, []);

  const activeCount = all.filter((job) => !isTerminal(job.status) && job.tenantId === tenantId).length;

  useEffect(() => {
    if (activeCount === 0 || tenantId === null) return;

    let cancelled = false;
    const pollFailures = new Map<string, number>();
    const tick = async (): Promise<void> => {
      // Only the current store's jobs: the X-Tenant-Id header follows the picker, so a
      // status call for another store's job would 404 and read as a failed export.
      const active = allRef.current.filter((job) => !isTerminal(job.status) && job.tenantId === tenantId);
      await Promise.all(
        active.map(async (job) => {
          try {
            const status = await fetchExportStatus(client, job.id);
            if (cancelled) return;
            const truncated = status.status === "ready" && status.error !== null;
            patch(job.id, {
              status: status.status,
              progress: status.progress,
              rowCount: status.rowCount,
              error: status.status === "failed" ? status.error : null,
              truncated,
            });
            pollFailures.delete(job.id);
            if (status.status === "ready" && !job.downloaded) {
              // One automatic download per job, at the moment it becomes ready.
              patch(job.id, { downloaded: true });
              await download(job.id).catch(() => {
                // The file exists; only this fetch failed. The Download button remains.
                patch(job.id, { downloaded: false });
              });
            }
          } catch (caught) {
            /**
             * One missed poll is a blip and the next tick retries — but a job we can
             * no longer ASK about must not spin forever. After several consecutive
             * misses the row says what happened instead of pretending to progress.
             */
            const misses = (pollFailures.get(job.id) ?? 0) + 1;
            pollFailures.set(job.id, misses);
            if (misses >= 5) {
              patch(job.id, {
                status: "failed",
                error: apiErrorMessage(caught, "Lost contact with this export — check My Exports."),
              });
            }
          }
        }),
      );
    };

    const timer = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeCount, tenantId, client, patch, download]);

  const value = useMemo<ExportsContextValue>(
    () => ({
      jobs: all.filter((job) => job.tenantId === tenantId),
      startExport,
      download,
      dismiss,
      clearFinished,
    }),
    [all, tenantId, startExport, download, dismiss, clearFinished],
  );

  return <ExportsContext.Provider value={value}>{children}</ExportsContext.Provider>;
}
